import React from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, Avatar, Button, Drawer, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import logoSymbol from '../assets/icons/logo-symbol.svg';
import {
  Dashboard,
  Chat,
  Description,
  FindInPage,
  Search,
  Logout,
  Settings,
  Article,
  Menu as MenuIcon,
  Close as CloseIcon,
  ChevronLeft,
  ChevronRight,
  History
} from '@mui/icons-material';

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { collapsed, setCollapsed, mobileOpen, setMobileOpen } = useSidebar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const menuItems = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: <Dashboard />,
      color: '#4ECDC4'
    },
    {
      label: 'Právní konzultace',
      path: '/consultation',
      icon: <Chat />,
      color: '#4ECDC4'
    },
    {
      label: 'Historie',
      path: '/consultation-history',
      icon: <History />,
      color: '#9C27B0',
      requireAuth: true
    },
    {
      label: 'Generování dokumentů',
      path: '/documents',
      icon: <Description />,
      color: '#FF6B6B'
    },
    {
      label: 'Analýza smluv',
      path: '/contract-analysis',
      icon: <FindInPage />,
      color: '#6C63FF'
    },
    {
      label: 'Právní výzkum',
      path: '/research',
      icon: <Search />,
      color: '#FFA726'
    },
    {
      label: 'Blog',
      path: '/blog',
      icon: <Article />,
      color: '#666666'
    }
  ];

  const isActive = (path: string) => {
    if (path === '/' && location.pathname !== '/') return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleMobileMenuItemClick = (path: string) => {
    setMobileOpen(false);
    navigate(path);
  };

  // Mobile Header Component
  const MobileHeader = () => (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '64px',
      backgroundColor: '#FFFFFF',
      borderBottom: '1px solid #E5E5E5',
      zIndex: 1100,
      display: { xs: 'flex', lg: 'none' },
      alignItems: 'center',
      justifyContent: 'space-between',
      px: 2
    }}>
      {/* Logo - navigate to homepage when clicked */}
      <Box
        onClick={() => navigate('/')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/'); } }}
        role="button"
        tabIndex={0}
        aria-label="Přejít na domovskou stránku"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
      >
        <Box 
          component="img" 
          src={logoSymbol} 
          alt="Logo" 
          sx={{ 
            width: '32px', 
            height: '32px'
          }} 
        />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography sx={{
              fontFamily: '"Rajdhani", sans-serif !important',
              fontSize: '18px',
              fontWeight: 400,
              color: '#1F1F1F',
              letterSpacing: '-0.5px'
            }}>
              Pravni
            </Typography>
            <Typography sx={{
              fontFamily: '"Rajdhani", sans-serif !important',
              fontSize: '18px',
              fontWeight: 700,
              color: '#1F1F1F',
              letterSpacing: '-0.5px'
            }}>
              BOT
            </Typography>
            <Typography sx={{
              fontFamily: '"Rajdhani", sans-serif !important',
              fontSize: '18px',
              fontWeight: 400,
              color: '#1F1F1F',
              letterSpacing: '-0.5px'
            }}>
              .cz
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '8px',
              fontWeight: 400,
              color: '#666',
              letterSpacing: '0.3px',
              marginTop: '-3px'
            }}
          >
            Patří do skupiny Apertia
          </Typography>
        </Box>
      </Box>
      
      {/* Hamburger Menu Button */}
      <IconButton
        onClick={() => setMobileOpen(true)}
        sx={{
          color: '#1F1F1F',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <MenuIcon />
      </IconButton>
    </Box>
  );

  // Sidebar Content Component
  const SidebarContent = ({ isMobileDrawer = false }) => (
    <>
      {/* Logo Section */}
      <Box sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        gap: collapsed && !isMobileDrawer ? 0 : 2,
        borderBottom: '1px solid #F0F0F0',
        justifyContent: collapsed && !isMobileDrawer ? 'center' : 'flex-start'
      }}>
        <Box
          onClick={() => navigate('/')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/'); } }}
          role="button"
          tabIndex={0}
          aria-label="Přejít na domovskou stránku"
          sx={{ display: 'flex', alignItems: 'center', gap: collapsed && !isMobileDrawer ? 0 : 2, cursor: 'pointer' }}
        >
          <Box 
            component="img" 
            src={logoSymbol} 
            alt="Logo" 
            sx={{ 
              width: '32px', 
              height: '32px'
            }} 
          />
        {(!collapsed || isMobileDrawer) && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
              <Typography sx={{
                fontFamily: '"Rajdhani", sans-serif !important',
                fontSize: '20px',
                fontWeight: 400,
                color: '#1F1F1F',
                letterSpacing: '-0.5px'
              }}>
                Pravni
              </Typography>
              <Typography sx={{
                fontFamily: '"Rajdhani", sans-serif !important',
                fontSize: '20px',
                fontWeight: 700,
                color: '#1F1F1F',
                letterSpacing: '-0.5px'
              }}>
                BOT
              </Typography>
              <Typography sx={{
                fontFamily: '"Rajdhani", sans-serif !important',
                fontSize: '20px',
                fontWeight: 400,
                color: '#1F1F1F',
                letterSpacing: '-0.5px'
              }}>
                .cz
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '9px',
                fontWeight: 400,
                color: '#666',
                letterSpacing: '0.3px',
                marginTop: '-3px'
              }}
            >
              Patří do skupiny Apertia
            </Typography>
          </Box>
        )}
        </Box>
      </Box>

      {/* User Section */}
      {isAuthenticated && user && (!collapsed || isMobileDrawer) && (
        <Box sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: '1px solid #F0F0F0'
        }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: '#1F1F1F',
              fontSize: '16px',
              fontWeight: 600
            }}
          >
            {user.username?.[0]?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 600,
              fontSize: '14px',
              color: '#1F1F1F'
            }}>
              {user.username}
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: '12px',
              color: '#666666'
            }}>
              Uživatel
            </Typography>
          </Box>
        </Box>
      )}

      {/* Collapsed User Avatar */}
      {isAuthenticated && user && collapsed && !isMobileDrawer && (
        <Box sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          borderBottom: '1px solid #F0F0F0'
        }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: '#1F1F1F',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            {user.username?.[0]?.toUpperCase() || 'U'}
          </Avatar>
        </Box>
      )}

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: collapsed && !isMobileDrawer ? 1 : 2 }}>
          {menuItems.map((item, index) => (
            <ListItem
              key={index}
              onClick={() => isMobileDrawer ? handleMobileMenuItemClick(item.path) : navigate(item.path)}
              sx={{
                borderRadius: '12px',
                mb: 1,
                cursor: 'pointer',
                backgroundColor: isActive(item.path) ? 'rgba(31, 31, 31, 0.06)' : 'transparent',
                '&:hover': {
                  backgroundColor: isActive(item.path) ? 'rgba(31, 31, 31, 0.08)' : 'rgba(31, 31, 31, 0.04)'
                },
                transition: 'all 0.4s ease-out',
                justifyContent: collapsed && !isMobileDrawer ? 'center' : 'flex-start',
                px: collapsed && !isMobileDrawer ? 1 : 2
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: collapsed && !isMobileDrawer ? 'auto' : '40px',
                color: isActive(item.path) ? item.color : '#666666',
                justifyContent: 'center'
              }}>
                {item.icon}
              </ListItemIcon>
              {(!collapsed || isMobileDrawer) && (
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '14px',
                    fontWeight: isActive(item.path) ? 600 : 400,
                    color: isActive(item.path) ? '#1F1F1F' : '#666666'
                  }}
                />
              )}
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Bottom Section */}
      <Box sx={{ p: collapsed && !isMobileDrawer ? 1 : 3, borderTop: '1px solid #F0F0F0' }}>
        {isAuthenticated ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(!collapsed || isMobileDrawer) ? (
              <>
                <Button
                  startIcon={<Settings />}
                  sx={{
                    justifyContent: 'flex-start',
                    px: 2,
                    py: 1,
                    color: '#666666',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 400,
                    '&:hover': {
                      backgroundColor: 'rgba(31, 31, 31, 0.04)'
                    }
                  }}
                >
                  Nastavení
                </Button>
                <Button
                  onClick={isMobileDrawer ? () => { setMobileOpen(false); handleLogout(); } : handleLogout}
                  startIcon={<Logout />}
                  sx={{
                    justifyContent: 'flex-start',
                    px: 2,
                    py: 1,
                    color: '#666666',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 400,
                    '&:hover': {
                      backgroundColor: 'rgba(31, 31, 31, 0.04)'
                    }
                  }}
                >
                  Odhlásit se
                </Button>
              </>
            ) : (
              <>
                <IconButton
                  sx={{
                    color: '#666666',
                    '&:hover': {
                      backgroundColor: 'rgba(31, 31, 31, 0.04)'
                    }
                  }}
                >
                  <Settings />
                </IconButton>
                <IconButton
                  onClick={handleLogout}
                  sx={{
                    color: '#666666',
                    '&:hover': {
                      backgroundColor: 'rgba(31, 31, 31, 0.04)'
                    }
                  }}
                >
                  <Logout />
                </IconButton>
              </>
            )}
          </Box>
        ) : (
          !collapsed || isMobileDrawer ? (
            <Button
              onClick={() => isMobileDrawer ? handleMobileMenuItemClick('/') : navigate('/')}
              fullWidth
              sx={{
                py: 1.5,
                bgcolor: '#1F1F1F !important',
                backgroundColor: '#1F1F1F !important',
                background: '#1F1F1F !important',
                color: '#FFFFFF !important',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                border: 'none !important',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#e72f0d !important',
                  backgroundColor: '#e72f0d !important',
                  background: '#e72f0d !important'
                }
              }}
            >
              Přihlásit se
            </Button>
          ) : null
        )}
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader />
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: '280px',
              backgroundColor: '#FFFFFF',
              borderRight: '1px solid #E5E5E5'
            }
          }}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid #F0F0F0'
          }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 600,
              fontSize: '16px',
              color: '#1F1F1F'
            }}>
              Menu
            </Typography>
            <IconButton
              onClick={() => setMobileOpen(false)}
              sx={{ color: '#666666' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <SidebarContent isMobileDrawer={true} />
        </Drawer>
      </>
    );
  }

  return (
    <Box sx={{
      width: collapsed ? '80px' : '280px',
      height: '100vh',
      backgroundColor: '#FFFFFF',
      borderRight: '1px solid #E5E5E5',
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      left: 0,
      top: 0,
      zIndex: 1000,
      transition: 'width 0.3s ease'
    }}>
      {/* Collapse Toggle Button */}
      <Box sx={{
        position: 'absolute',
        right: '-12px',
        top: '20px',
        zIndex: 1001
      }}>
        <IconButton
          onClick={() => setCollapsed(!collapsed)}
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            width: '24px',
            height: '24px',
            '&:hover': {
              backgroundColor: '#F5F5F5'
            }
          }}
        >
          {collapsed ? <ChevronRight sx={{ fontSize: '14px' }} /> : <ChevronLeft sx={{ fontSize: '14px' }} />}
        </IconButton>
      </Box>

      <SidebarContent />
    </Box>
  );
};

export default Sidebar;
